import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Invoice, InvoiceStatus, Client, Project } from '../types';
import invoiceService from '../services/invoiceService';
import clientService from '../services/clientService';
import projectService from '../services/projectService';
import businessAreaService from '../services/businessAreaService';
import { PageHeader, Badge, Spinner } from '../components/ui';
import { usePageTitle } from '../hooks/usePageTitle';

const statusVariant = (s: InvoiceStatus): 'green' | 'amber' | 'purple' | 'red' => {
  switch (s) {
    case InvoiceStatus.DRAFT: return 'amber';
    case InvoiceStatus.SENT: return 'purple';
    case InvoiceStatus.PAID: return 'green';
    case InvoiceStatus.OVERDUE: return 'red';
    case InvoiceStatus.CANCELLED: return 'red';
  }
};

const statusLabel = (s: InvoiceStatus): string => ({
  [InvoiceStatus.DRAFT]: 'Entwurf',
  [InvoiceStatus.SENT]: 'Versendet',
  [InvoiceStatus.PAID]: 'Bezahlt',
  [InvoiceStatus.OVERDUE]: 'Überfällig',
  [InvoiceStatus.CANCELLED]: 'Storniert',
}[s]);

const fmtDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('de-DE') : '—';

const Invoices: React.FC = () => {
  usePageTitle('Rechnungen');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filterClient, setFilterClient] = useState<number | ''>('');
  const [filterProject, setFilterProject] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<InvoiceStatus | ''>('');
  const [search, setSearch] = useState('');
  // Aus der URL, damit die Dashboard-Karte direkt auf ein Vorhaben verlinken kann und der Filter
  // einen Reload und einen geteilten Link übersteht.
  const [searchParams, setSearchParams] = useSearchParams();
  const filterArea = searchParams.get('businessArea') ?? '';
  const [areas, setAreas] = useState<string[]>([]);

  const load = async () => {
    try {
      setLoading(true);
      const [invList, clientList] = await Promise.all([
        invoiceService.getAll({
          clientId: filterClient || undefined,
          projectId: filterProject || undefined,
          status: filterStatus || undefined,
          businessArea: filterArea || undefined,
        }),
        clientService.getAll(),
      ]);
      setInvoices(invList);
      setClients(clientList);
    } catch (e) {
      toast.error('Rechnungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterClient, filterProject, filterStatus, filterArea]);

  // Die Auswahl zeigt nur, was es gibt; ohne vergebene Bereiche fällt sie ganz weg.
  useEffect(() => { businessAreaService.list().then(setAreas).catch(() => setAreas([])); }, []);

  useEffect(() => {
    if (!filterClient) { setProjects([]); setFilterProject(''); return; }
    projectService.getAll(Number(filterClient)).then(setProjects).catch(() => setProjects([]));
  }, [filterClient]);

  /**
   * Ordnet eine Rechnung einem Vorhaben zu.
   *
   * Geht auch bei festgeschriebenen Rechnungen — der Bereich steht nicht auf dem Dokument. Genau
   * dafür gibt es das Feld: die schon gestellten Rechnungen sind der Bestand, der zugeordnet
   * werden muss, und die sind alle festgeschrieben.
   */
  const saveArea = async (invoice: Invoice, area: string | null) => {
    try {
      await invoiceService.setBusinessArea(invoice.id!, area);
      setInvoices(list => list.map(x => (x.id === invoice.id ? { ...x, businessArea: area } : x)));
      if (area && !areas.includes(area)) setAreas(list => [...list, area].sort());
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Das Vorhaben ließ sich nicht speichern');
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return invoices;
    const q = search.toLowerCase();
    return invoices.filter(i =>
      i.number.toLowerCase().includes(q) ||
      i.title.toLowerCase().includes(q) ||
      (i.clientName ?? '').toLowerCase().includes(q)
    );
  }, [invoices, search]);

  return (
    <div className="p-6 sm:p-8 max-w-7xl mx-auto">
      <PageHeader
        title="Rechnungen"
        subtitle="Rechnungen erstellen, verwalten und exportieren"
        action={
          <button
            onClick={() => navigate('/invoices/new')}
            className="btn-primary bg-brand-600 hover:bg-brand-700 text-white px-5 py-2.5 rounded-lg font-semibold text-sm shadow-sm"
          >
            + Neue Rechnung
          </button>
        }
      />

      {/* Einmal im Dokument, von jeder Zeile der Spalte „Vorhaben" genutzt. */}
      <datalist id="invoice-business-areas">
        {areas.map(a => <option key={a} value={a} />)}
      </datalist>

      <div className="flex flex-wrap gap-3 mb-6">
        <input
          type="text"
          placeholder="Suche (Nummer, Titel, Kunde)…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="px-3 py-2 rounded-lg border text-sm flex-1 min-w-[200px]"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        />
        <select
          value={filterClient}
          onChange={e => setFilterClient(e.target.value === '' ? '' : Number(e.target.value))}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        >
          <option value="">Alle Kunden</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          value={filterProject}
          onChange={e => setFilterProject(e.target.value === '' ? '' : Number(e.target.value))}
          disabled={!filterClient}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)', opacity: filterClient ? 1 : 0.5 }}
        >
          <option value="">Alle Projekte</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {areas.length > 0 && (
          <select
            value={filterArea}
            onChange={e => {
              const next = new URLSearchParams(searchParams);
              if (e.target.value) next.set('businessArea', e.target.value); else next.delete('businessArea');
              setSearchParams(next, { replace: true });
            }}
            className="px-3 py-2 rounded-lg border text-sm"
            style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
            aria-label="Vorhaben"
          >
            <option value="">Alle Vorhaben</option>
            {areas.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        )}
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as InvoiceStatus | '')}
          className="px-3 py-2 rounded-lg border text-sm"
          style={{ background: 'var(--surface-bg)', borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
        >
          <option value="">Alle Stati</option>
          {Object.values(InvoiceStatus).map(s => (
            <option key={s} value={s}>{statusLabel(s)}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>
          Keine Rechnungen gefunden.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: 'var(--surface-border)' }}>
          <table className="w-full text-sm">
            <thead style={{ background: 'var(--surface-bg)', color: 'var(--text-muted)' }}>
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Nummer</th>
                <th className="text-left px-4 py-3 font-semibold">Titel</th>
                <th className="text-left px-4 py-3 font-semibold">Kunde</th>
                <th className="text-left px-4 py-3 font-semibold">Datum</th>
                <th className="text-left px-4 py-3 font-semibold">Fällig</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Vorhaben</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr
                  key={i.id}
                  onClick={() => navigate(`/invoices/${i.id}`)}
                  className="cursor-pointer transition-colors hover:opacity-80 border-t"
                  style={{ borderColor: 'var(--surface-border)', color: 'var(--text-primary)' }}
                >
                  <td className="px-4 py-3 font-mono font-semibold">{i.number}</td>
                  <td className="px-4 py-3">{i.title}</td>
                  <td className="px-4 py-3">{i.clientName}</td>
                  <td className="px-4 py-3">{fmtDate(i.invoiceDate)}</td>
                  <td className="px-4 py-3">{fmtDate(i.dueDate)}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant(i.status)}>{statusLabel(i.status)}</Badge>
                  </td>
                  {/* Zuordnen direkt in der Liste, nicht nur in der Rechnungsansicht: den Bestand
                      geht man am Stück durch, und dafür ist die Liste der Ort — in der Detailseite
                      liegt das Feld hinter allen Positionen, wo es beim Nachpflegen niemand sucht.
                      Der Klick darf die Zeile nicht öffnen, deshalb stopPropagation. */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <input
                      list="invoice-business-areas"
                      type="text"
                      defaultValue={i.businessArea ?? ''}
                      placeholder="—"
                      onBlur={e => {
                        const next = e.target.value.trim() === '' ? null : e.target.value.trim();
                        if (next !== (i.businessArea ?? null)) saveArea(i, next);
                      }}
                      className="px-2 py-1 rounded text-xs border w-36"
                      style={{
                        background: 'var(--input-bg)',
                        borderColor: 'var(--surface-border)',
                        color: 'var(--text-primary)',
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Invoices;
